import { Alert, Button, OTPInput, Text } from '@restworld/ui-ds-base';
import { memo, useEffect } from 'react';
import { useCountDownTimer } from '@restworld/utils-common';
import { AuthTitle, authPageTitleClassName } from '../common/auth-title';
import { authPageSubtitleClassName } from '../common/auth-subtitle';
import { useEmailLoginForm } from './hooks/use-email-login-form';
import { useEmailOtpForm } from './hooks/use-email-otp-form';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  ContentType as ModalContentType,
  closeAuthFlowModal,
  closeEmailTokenSentModal,
  emailVerificationDetails,
  setActiveContent as setActiveModalContent,
  updateEmailVerificationDetails,
} from '../../context/auth-flow';
import { useTranslation } from 'next-i18next';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import { webmailUrlFor } from './webmail';

export type LoginLinkSentModalProps = {
  isModal?: boolean;
  /** The modal closes itself here; the login and signup pages pass nothing. */
  onAcknowledgingEmailReception?: () => void;
};

// The login email carries BOTH a 6-digit code and a magic link. Neither is
// deprecated: workers tend to use the link, employers the code. Both are shown
// stacked (code on top, link below), so each stays visible.
//
// In modal mode the form owns a fixed-header / scrollable-body / fixed-footer
// layout so it never overflows the viewport. In non-modal mode (the desktop
// login page and signup) the caller renders <LoginLinkSentHeader/> itself, so
// the form only renders the body + resend in natural page flow.
export const LoginLinkSentForm = memo(({ isModal, onAcknowledgingEmailReception }: LoginLinkSentModalProps) => {
  const emailDetails = useAtomValue(emailVerificationDetails);
  const { t } = useTranslation('auth-flow');

  const { hasExpired: waitTimerExpired, resetTimer: resetWaitTimer } = useCountDownTimer({
    timeInSeconds: emailDetails.waitTime,
    expiresAt: emailDetails.waitExpiresAt,
  });
  const {
    label: tokenTimerLabel,
    hasExpired: tokenHasExpired,
    resetTimer: resetCountDownTimer,
  } = useCountDownTimer({
    timeInSeconds: emailDetails.tokenValidityTime,
    expiresAt: emailDetails.tokenExpiresAt,
  });

  const { loading, onSubmit, remainingTimes } = useEmailLoginForm({ isModal });

  // Email-OTP code entry (auto-submits when all 6 digits are in).
  const {
    verify,
    loading: verifying,
    isSuccess,
    serverValidationFailed,
    setServerValidationFailed,
    tooManyAttempts,
    setTooManyAttempts,
    otpValue,
    setOtpValue,
    errorMessage,
  } = useEmailOtpForm({ email: emailDetails.email, isModal });

  const resetEmailDetails = useSetAtom(updateEmailVerificationDetails);
  const closeEmailSentModal = useSetAtom(closeEmailTokenSentModal);
  const closeAuthFlow = useSetAtom(closeAuthFlowModal);
  const setActiveContent = useSetAtom(setActiveModalContent);

  // "Apri email" opens the worker's own webmail (see webmail.ts). On the pages it used to call
  // an empty handler: tapped, and nothing happened (Andrea's report of 05/10/2026, item 9). An
  // address we cannot map gets no button on the pages, rather than one that does nothing.
  const webmailUrl = webmailUrlFor(emailDetails.email);
  const openEmail = () => {
    if (webmailUrl) window.open(webmailUrl, '_blank', 'noopener,noreferrer');
    onAcknowledgingEmailReception?.();
  };

  useEffect(() => {
    if (remainingTimes > 0) {
      resetWaitTimer();
    }
    if (remainingTimes >= 0) {
      resetCountDownTimer();
    }
  }, [remainingTimes, resetCountDownTimer, resetWaitTimer]);

  // Second reset path (besides a page refresh): once the TTL runs out the code
  // and link are dead, so clear the persisted state and close the modal. The
  // next sign-in then starts fresh at email entry. Modal-only — the non-modal
  // page has no modal to close.
  useEffect(() => {
    if (!isModal || !tokenHasExpired) return;
    resetEmailDetails({
      email: '',
      waitTime: 0,
      tokenValidityTime: 0,
      tokenExpiresAt: undefined,
      waitExpiresAt: undefined,
    });
    setActiveContent(ModalContentType.NO_CONTENT);
    closeEmailSentModal();
    closeAuthFlow();
  }, [isModal, tokenHasExpired, resetEmailDetails, setActiveContent, closeEmailSentModal, closeAuthFlow]);

  // Code, "oppure" divider, magic-link banner with its expiry, and the resend
  // action, which only appears once the wait between sends is over. Same content
  // on the login page and in the modal. The wait is the backend's wait_time: a new email asked
  // for before it ends is refused, so the too-many-attempts lock waits for it too.
  const canResend = (waitTimerExpired || tokenHasExpired) && remainingTimes !== 0;
  const content = (
    <div className="flex flex-col items-center w-full">
      <OTPInput
        value={otpValue}
        isSuccess={isSuccess}
        variant={serverValidationFailed ? 'error' : 'primary'}
        // Every code is refused until a new email is requested.
        disabled={tooManyAttempts}
        onChange={(otp) => {
          setOtpValue(otp);
          if (serverValidationFailed) setServerValidationFailed(false);
          if (otp.length === 6) verify(otp);
        }}
      />
      {serverValidationFailed && errorMessage && (
        <p className="mt-2 text-sm text-red-600 text-center">{errorMessage}</p>
      )}
      <Button
        className="mt-4"
        fullwidth={true}
        loading={verifying}
        disabled={otpValue.length !== 6 || verifying || tooManyAttempts}
        onClick={() => verify(otpValue)}
      >
        {t('email_login_otp_submit')}
      </Button>

      {/* No Divider in ui-ds-base: hand-built line + label. */}
      <div className="flex flex-row items-center w-full gap-3 mt-6">
        <div className="h-px bg-neutral-40 flex-1" />
        <span className="text-sm text-neutral-700 whitespace-nowrap">{t('email_login_or')}</span>
        <div className="h-px bg-neutral-40 flex-1" />
      </div>

      <Alert
        className="mt-6 w-full"
        variant="restworld"
        icon={<InformationCircleIcon className="text-purple-700 min-w-4 w-4 h-4" />}
        textClassName="text-sm lg:text-sm leading-5 lg:leading-5 font-normal"
        text={
          // Locked out, the link from this email was revoked together with the code.
          tokenHasExpired || tooManyAttempts ? (
            t('email_login_magic_link_banner_expired')
          ) : (
            <>
              {t('email_login_magic_link_banner_text')} ({t('email_login_magic_link_banner_expires_in')}{' '}
              <strong className="font-semibold">{tokenTimerLabel}</strong>)
            </>
          )
        }
      />

      {/* "Apri email" opens the worker's webmail (Andrea's report 05/10/2026, item 9); hidden when
          the provider is unknown and there is nothing to close, so it never does nothing. */}
      {(webmailUrl || onAcknowledgingEmailReception) && (
        <Button className="mt-6" fullwidth={true} onClick={openEmail}>
          <span className="text-black">{t('email_login_email_link_sent_link_email_received_text')}</span>
        </Button>
      )}

      {canResend && (
        <div className="flex flex-col items-center w-full gap-1 mt-6">
          <Button
            variant="ghost"
            loading={loading}
            onClick={() => {
              // A new email lifts the lock on the backend, so the form can take a code again.
              setTooManyAttempts(false);
              setServerValidationFailed(false);
              setOtpValue('');
              onSubmit({ email: emailDetails.email });
            }}
            data-cy="login-resend-code"
          >
            {t('email_login_resend_code')}
          </Button>
          {remainingTimes < 3 && (
            <Text color="muted">
              {remainingTimes} {t('email_login_email_link_sent_resent_count')}
            </Text>
          )}
        </div>
      )}
    </div>
  );

  if (!isModal) return content;

  // Modal: the header is rendered here; the whole step scrolls if the viewport is short.
  return (
    <div className="flex flex-col w-full gap-8 mt-5 max-h-[75vh] lg:max-h-[82vh] overflow-y-auto">
      <LoginLinkSentHeader isModal />
      {content}
    </div>
  );
});

export const LoginLinkSentHeader = (_props: { isModal?: boolean }) => {
  const { t } = useTranslation('auth-flow');
  const { email } = useAtomValue(emailVerificationDetails);

  return (
    <div className="flex flex-col items-center w-full gap-4">
      <AuthTitle className={authPageTitleClassName} data-cy="login-link-sent-heading">
        {t('email_login_page_otp_header')}
      </AuthTitle>
      <p className={authPageSubtitleClassName}>
        {t('email_login_page_otp_description')} <strong className="font-semibold">{email}</strong>.
        <br />({t('email_login_page_otp_spam_hint')})
      </p>
    </div>
  );
};
